import React, { useEffect, useRef } from 'react';
import { getDefaultCSSVariables } from '../../../config/defaultTheme';
import styles from './ThemePreviewModal.module.css';

interface ThemePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeConfig: any;
  device: 'desktop' | 'mobile';
}

export const ThemePreviewModal: React.FC<ThemePreviewModalProps> = ({
  isOpen,
  onClose,
  themeConfig,
  device,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isOpen && iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        // Inject CSS variables
        const cssVariables = generateCSSVariables(themeConfig);
        doc.open();
        doc.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                ${cssVariables}
                padding: 2rem;
                background: var(--color-bgPrimary);
                color: var(--color-textPrimary);
              }
              .preview-container {
                max-width: ${device === 'desktop' ? '1200px' : '100%'};
                margin: 0 auto;
              }
              .header {
                height: ${themeConfig?.layout?.headerHeight || '44px'};
                background: var(--color-bgSecondary);
                border: 1px solid var(--color-borderPrimary);
                border-radius: var(--radius-sm, 8px);
                display: flex;
                align-items: center;
                padding: 0 1rem;
                margin-bottom: 1rem;
              }
              .card {
                background: var(--color-bgSecondary);
                border: 1px solid var(--color-borderPrimary);
                border-radius: var(--radius-md, 12px);
                padding: 1.5rem;
                margin-bottom: 1rem;
              }
              .button {
                padding: 0.75rem 1.5rem;
                background: var(--color-gradientPrimary);
                color: #fff;
                border: none;
                border-radius: var(--radius-sm, 8px);
                cursor: pointer;
                font-size: 1rem;
              }
              .button:hover {
                filter: brightness(1.1);
              }
            </style>
          </head>
          <body>
            <div class="preview-container">
              <div class="header">
                <strong>${themeConfig?.brand?.siteName || 'Site Name'}</strong>
              </div>
              <div class="card">
                <h2 style="margin-bottom: 1rem;">Preview Card</h2>
                <p style="margin-bottom: 1rem; color: var(--color-textSecondary);">
                  This is a preview of your theme configuration. The colors, fonts, and spacing
                  reflect your current settings.
                </p>
                <button class="button">Primary Button</button>
              </div>
            </div>
          </body>
          </html>
        `);
        doc.close();
      }
    }
  }, [isOpen, themeConfig, device]);

  if (!isOpen) return null;

  const generateCSSVariables = (config: any): string => {
    if (!config) {
      // Return default CSS variables from shared config
      return getDefaultCSSVariables();
    }
    const colors = config.colors || {};
    const spacing = config.spacing || {};

    return `
      --color-primary: ${colors.primary || '#C4A484'};
      --color-primaryHover: ${colors.primaryHover || '#E8D5C4'};
      --color-primaryDark: ${colors.primaryDark || '#A88B6E'};
      --color-bgPrimary: ${colors.bgPrimary || '#FAF8F5'};
      --color-bgSecondary: ${colors.bgSecondary || '#F5F1EA'};
      --color-textPrimary: ${colors.textPrimary || '#3E3328'};
      --color-textSecondary: ${colors.textSecondary || '#6D5F52'};
      --color-borderPrimary: ${colors.borderPrimary || '#E8E2D8'};
      --color-gradientPrimary: ${colors.gradientPrimary || 'linear-gradient(135deg, #E8D5C4, #C4A484)'};
      --radius-sm: ${spacing.borderRadiusSmall || '8px'};
      --radius-md: ${spacing.borderRadiusMedium || '12px'};
    `;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Theme Preview - {device === 'desktop' ? 'Desktop' : 'Mobile'}</h2>
          <button onClick={onClose} className={styles.closeButton}>✕</button>
        </div>
        <div className={styles.modalBody}>
          <iframe
            ref={iframeRef}
            title="Theme Preview"
            className={styles.previewFrame}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
};
